import { ChevronRight, Download, QrCode, Wallet } from 'lucide-react'
import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { useLocation } from 'react-router-dom'
import { useAccount, useConnect, useDisconnect, useSwitchChain, type Connector } from 'wagmi'
import { useAuth } from '@/hooks/useAuth'
import { ReferralPrompt } from '@/features/referral/ReferralPrompt'
import { CONNECTOR_META, addChainParams, requiredChainFor, walletConnectEnabled } from '@/lib/wagmi'
import { Callout, Modal } from '@/ui/primitives'

/**
 * ONE wallet state machine, shared by every connect affordance in the app.
 *
 * ── Why this exists ──
 * There are two places a user can be asked to connect: the header button and the
 * perps order ticket. They had independent logic, and it showed:
 *
 *   • The ticket only checked `isConnected`. A user connected to BSC saw
 *     "Deposit to Start" — a trading action — while the header simultaneously
 *     demanded a switch to Arbitrum. Two components, two contradictory answers.
 *   • The ticket was blind to SIWE entirely, so a connected-but-unauthenticated
 *     user got a trading CTA that could not possibly work.
 *   • The ticket's connect silently grabbed `connectors[0]`, bypassing the picker
 *     the header shows — so which wallet you got depended on where you clicked.
 *
 * Any new connect affordance should read `useWalletGate()` rather than reinvent
 * this, or the same divergence grows back.
 */

export type WalletState =
  | 'reconnecting' // wagmi is rehydrating a stored session
  | 'disconnected' // no wallet
  | 'locked' // connected but no account exposed (locked / permission revoked)
  | 'wrong-chain' // connected to a chain this route cannot use
  | 'ready' // good to transact

interface WalletGateValue {
  state: WalletState
  address: `0x${string}` | undefined
  /**
   * SIWE session status. Deliberately NOT part of `state`.
   *
   * Bonding, approving and claiming are signed by the wallet and validated by the
   * contract — a backend session is irrelevant to whether they can succeed. When
   * SIWE was folded into the state machine, `needs-signin` sat between the user and
   * every on-chain button, so an unreachable API made the whole app unusable to a
   * fully-connected wallet holding funds. Sign-in is now an optional capability
   * that unlocks backend features, never a prerequisite for touching the chain.
   */
  isSignedIn: boolean
  needsReferral: boolean
  /** Authoritative session state says this wallet has a real referring wallet. */
  hasReferralAccess: boolean
  /** Connected but not signed in — show an offer, never a block. */
  canSignIn: boolean
  /**
   * The wallet's own referral code.
   *
   * Exposed because the partner hand-off needs it: their login answers an address that
   * maps to several accounts with `need_account_select` rather than a session, and the
   * code is what disambiguates.
   */
  referralCode: string | null
  signInError: Error | null
  signingIn: boolean
  signOut: () => void
  /**
   * The chain THIS ROUTE requires, or null when the route accepts any supported
   * chain (Swap). Consumers must handle null rather than assuming a requirement.
   */
  requiredChain: ReturnType<typeof requiredChainFor>
  isBusy: boolean
  /** Opens the connector picker. Always prefer this over calling connect() directly. */
  openPicker: () => void
  switchToRequired: () => void
  /** Non-null while the last switch attempt is failed and unacknowledged. */
  switchError: Error | null
  signIn: () => void
  /** One short sentence naming what the user must do next, or null when ready. */
  actionLabel: string | null
  /** Runs the single correct next action for the current state. */
  runAction: () => void
}

const WalletGateContext = createContext<WalletGateValue | null>(null)

export function useWalletGate(): WalletGateValue {
  const ctx = useContext(WalletGateContext)
  if (!ctx) throw new Error('useWalletGate must be used inside <WalletGateProvider>')
  return ctx
}

export function WalletGateProvider({ children }: { children: ReactNode }) {
  const { address, isConnected, isReconnecting, chainId } = useAccount()
  const { switchChainAsync, isPending: switching, error: switchError } = useSwitchChain()
  /*
   * The ONLY `useAuth()` call in the app.
   *
   * `useAuth` holds the session in local `useState` and kicks off a silent refresh
   * on mount, so every extra call site created a second, independent session — the
   * header ran one and this provider ran another. Signing in through one left the
   * other still showing "Sign in", and page load fired two refreshes. Auth is
   * re-exported through this context for the same reason chain state is: one source,
   * or the copies drift.
   */
  const {
    isSignedIn,
    canSignIn,
    signIn,
    signingIn,
    signInError,
    signOut,
    needsReferral,
    referralCode,
    markReferralSettled,
  } = useAuth()
  const { pathname } = useLocation()
  const [pickerOpen, setPickerOpen] = useState(false)

  const requiredChain = requiredChainFor(pathname)

  const state: WalletState = useMemo(() => {
    if (isReconnecting) return 'reconnecting'
    if (!isConnected) return 'disconnected'
    if (!address) return 'locked'
    // No requirement (Swap) means no wrong-chain state — the bridge handles it.
    if (requiredChain && chainId !== requiredChain.id) return 'wrong-chain'
    return 'ready'
  }, [isReconnecting, isConnected, address, chainId, requiredChain])

  const openPicker = useCallback(() => setPickerOpen(true), [])

  /**
   * Switch, adding the chain to the wallet if it does not know it.
   *
   * Most wallets ship BNB mainnet and Arbitrum but not BNB testnet, so a plain
   * `wallet_switchEthereumChain` returns 4902 and the button appeared to do nothing.
   * Passing `addEthereumChainParameter` lets wagmi fall back to
   * `wallet_addEthereumChain` in the same click, which is what a user pressing
   * "Switch to BNB Testnet" is actually asking for.
   */
  const switchToRequired = useCallback(() => {
    if (!requiredChain) return
    void switchChainAsync({
      chainId: requiredChain.id,
      addEthereumChainParameter: addChainParams(requiredChain),
      // Rejecting a switch is a decision, not a crash. The error is rendered from
      // `switchError`; an unhandled rejection here would just noise the console.
    }).catch(() => {})
  }, [switchChainAsync, requiredChain])

  const doSignIn = useCallback(() => void signIn().catch(() => {}), [signIn])

  /*
   * Auto sign-in is NOT done here. `useAuth` already offers SIWE the moment a wallet
   * connects, with a per-wallet marker in sessionStorage so it fires exactly once.
   *
   * A second attempt was added here and had to be removed: both effects called
   * `signIn.mutateAsync()`, each fetched its own nonce, and the second invalidated the
   * first — which surfaced as an intermittent "Sign-in failed" on a screen the user had
   * not touched.
   */

  const actionLabel = useMemo(() => {
    switch (state) {
      case 'reconnecting':
        return 'Reconnecting…'
      case 'disconnected':
        // Sentence case, matching the app bar's own button and every other label in the
        // product. This read 'Connect Wallet' while the bar read 'Connect wallet', so the two
        // disagreed wherever they appeared together — which is every page.
        return 'Connect wallet'
      case 'locked':
        return 'Unlock your wallet'
      case 'wrong-chain':
        return requiredChain ? `Switch to ${requiredChain.name}` : null
      case 'ready':
        return null
    }
  }, [state, requiredChain])

  const runAction = useCallback(() => {
    switch (state) {
      case 'disconnected':
        openPicker()
        break
      case 'wrong-chain':
        switchToRequired()
        break
      // 'reconnecting' resolves itself; 'locked' needs action in the wallet, not here.
      default:
        break
    }
  }, [state, openPicker, switchToRequired])

  const value: WalletGateValue = {
    state,
    address,
    isSignedIn,
    needsReferral,
    hasReferralAccess: isSignedIn && !needsReferral,
    canSignIn,
    referralCode,
    signInError,
    signingIn,
    signOut: () => void signOut(),
    requiredChain,
    // Sign-in no longer blocks, so it must not make the whole app look busy either.
    isBusy: switching || state === 'reconnecting',
    openPicker,
    switchToRequired,
    switchError,
    signIn: doSignIn,
    actionLabel,
    runAction,
  }

  return (
    <WalletGateContext.Provider value={value}>
      {children}
      {/* Single picker instance for the whole app, so both call sites open the
          same modal with the same wallet list. */}
      <ConnectorPicker open={pickerOpen} onClose={() => setPickerOpen(false)} />
      {/*
        Mounted here, beside the picker, for the same reason: this provider is the one
        place that owns the session, so the prompt cannot end up rendered twice or
        rendered against a stale copy of the session.

        There is no local "dismissed" flag and no default referral. The prompt remains
        until a valid code is durably bound, so `needsReferral` going false IS the record that the
        user has been asked — which means the answer survives a reload, a new device and
        a re-sign-in, rather than living in component state that a refresh would forget.
      */}
      <ReferralPrompt
        open={isSignedIn && needsReferral}
        ownCode={referralCode}
        onSettled={markReferralSettled}
      />
    </WalletGateContext.Provider>
  )
}

/** Where to send a user who has no wallet at all. */
const INSTALL_LINKS = [
  { name: 'MetaMask', url: 'https://metamask.io/download/' },
  { name: 'Trust Wallet', url: 'https://trustwallet.com/download' },
  { name: 'Rabby', url: 'https://rabby.io/' },
]

function ConnectorPicker({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { connector: activeConnector, isConnected } = useAccount()
  const { disconnectAsync } = useDisconnect()
  const { connectAsync, connectors, isPending, variables, error, reset } = useConnect()

  /**
   * Selecting a wallet while one is already connected.
   *
   * wagmi throws "Connector already connected" if you call connect() on the active
   * connector, and leaves the old session in place if you connect a different one.
   * Both surfaced as red error text under the list — for an action the user was
   * right to take. Handle the three cases explicitly instead:
   *
   *   same wallet      → nothing to do, just close
   *   different wallet → disconnect first, then connect (a clean switch)
   *   none connected   → connect
   */
  const choose = useCallback(
    async (connector: Connector) => {
      if (isConnected && activeConnector?.uid === connector.uid) {
        onClose()
        return
      }
      try {
        if (isConnected) await disconnectAsync()
        await connectAsync({ connector })
        onClose()
      } catch {
        // Surfaced by `error` below; a rejected request is not exceptional.
      }
    },
    [isConnected, activeConnector, disconnectAsync, connectAsync, onClose],
  )

  /**
   * De-duplicate by connector `id`, NOT by `type`.
   *
   * wagmi discovers injected wallets via EIP-6963 and creates one connector per
   * wallet — MetaMask, Rabby, Phantom and Coinbase all arrive with type
   * `'injected'` but distinct ids. Deduping by type collapsed them into a single
   * generic row, so a user with three wallets installed could only ever reach
   * whichever one wagmi happened to list first.
   */
  const options = useMemo(() => {
    const seen = new Set<string>()
    const unique = connectors.filter((c) => {
      if (seen.has(c.id)) return false
      seen.add(c.id)
      return true
    })

    /*
     * Drop the GENERIC injected fallback when EIP-6963 has discovered real wallets.
     *
     * wagmi ships a catch-all connector with id `'injected'`, and separately
     * announces each installed wallet with its own id (`io.metamask`,
     * `com.trustwallet.app`, …). Listing both showed a nameless "Injected" row
     * sitting above "MetaMask" and "Trust Wallet" — three rows, two of which were
     * the same wallet, and the generic one had no icon so it looked like the
     * broken option. Keep the fallback only when nothing was discovered, which is
     * the case it exists for.
     */
    const discovered = unique.filter((c) => c.type === 'injected' && c.id !== 'injected')
    return discovered.length > 0 ? unique.filter((c) => c.id !== 'injected') : unique
  }, [connectors])

  const hasInjected = options.some((c) => c.type === 'injected')

  return (
    <Modal
      open={open}
      onClose={() => {
        reset()
        onClose()
      }}
      title="Connect a wallet"
    >
      <div className="space-y-2">
        {options.map((connector) => (
          <ConnectorRow
            key={connector.uid}
            connector={connector}
            pending={isPending && variables?.connector === connector}
            // Any pending connection disables the rest: a second
            // eth_requestAccounts while one is open makes the wallet throw
            // "already processing" — an error the user caused by doing something
            // entirely reasonable.
            disabled={isPending}
            onClick={() => void choose(connector)}
          />
        ))}
      </div>

      {!hasInjected && (
        <div className="mt-4">
          <Callout tone="warning" title="No browser wallet detected">
            <p>Install one of these, then reload this page:</p>
            <ul className="mt-2 flex flex-wrap gap-3">
              {INSTALL_LINKS.map((w) => (
                <li key={w.name}>
                  <a
                    href={w.url}
                    target="_blank"
                    rel="noreferrer noopener"
                    className="inline-flex items-center gap-1 font-medium text-accent underline underline-offset-4"
                  >
                    <Download className="size-3" aria-hidden />
                    {w.name}
                  </a>
                </li>
              ))}
            </ul>
            {walletConnectEnabled && (
              <p className="mt-2">Or use WalletConnect above to pair a wallet on your phone.</p>
            )}
          </Callout>
        </div>
      )}

      {!walletConnectEnabled && (
        <div className="mt-4">
          <Callout tone="warning" title="WalletConnect is not configured">
            Set <code className="rounded bg-elevated px-1">VITE_WALLETCONNECT_PROJECT_ID</code> in{' '}
            <code className="rounded bg-elevated px-1">.env</code> with a free project ID from
            cloud.reown.com, then restart the dev server.
          </Callout>
        </div>
      )}

      {error && <p className="mt-4 text-xs text-danger">{connectErrorMessage(error)}</p>}

      <p className="mt-4 text-xs leading-relaxed text-faint">
        Connecting only shares your public address. You will then be asked to sign a message to
        prove ownership — that signature authorizes no transaction, transfer or token approval.
      </p>
    </Modal>
  )
}

function ConnectorRow({
  connector,
  pending,
  disabled,
  onClick,
}: {
  connector: Connector
  pending: boolean
  disabled: boolean
  onClick: () => void
}) {
  /*
   * A discovered wallet announces its own name and icon, so it must NOT inherit
   * the generic injected copy — every row was reading "MetaMask, Trust, Binance
   * Wallet, OKX…" under headings that already said MetaMask or Trust Wallet.
   */
  const isDiscovered = connector.type === 'injected' && connector.id !== 'injected'
  const meta = CONNECTOR_META[connector.type]
  const label = isDiscovered ? connector.name : (meta?.label ?? connector.name)
  const hint = isDiscovered
    ? 'Browser extension or in-app wallet'
    : (meta?.hint ?? 'Connect with this wallet')

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className="flex w-full items-center gap-3 rounded-md p-3 text-left ring-1 ring-line transition-colors hover:bg-elevated disabled:cursor-not-allowed disabled:opacity-50"
    >
      <span className="grid size-9 shrink-0 place-items-center overflow-hidden rounded-sm bg-accent/12 text-accent">
        {connector.icon ? (
          <img src={connector.icon} alt="" className="size-5 rounded" />
        ) : connector.type === 'walletConnect' ? (
          <QrCode className="size-4" />
        ) : (
          <Wallet className="size-4" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium">{label}</span>
        <span className="mt-0.5 block text-xs text-muted">{hint}</span>
      </span>
      {pending ? (
        <span className="shrink-0 text-xs text-muted">Check your wallet…</span>
      ) : (
        <ChevronRight className="size-4 shrink-0 text-faint" />
      )}
    </button>
  )
}

// ─────────────────────────── Error copy ───────────────────────────
// Wallet errors are written for developers. These map the ones users actually hit
// onto sentences that say what to do next.

export function connectErrorMessage(error: Error): string | null {
  const m = error.message
  // Handled by `choose()` above — never worth showing the user.
  if (/already connected/i.test(m)) return null
  if (/user rejected|user denied/i.test(m)) return 'Connection request was rejected in your wallet.'
  if (/already pending|already processing/i.test(m))
    return 'Your wallet already has a pending request — open it and approve or dismiss that first.'
  if (/connector not found|no injected|provider not found/i.test(m))
    return 'That wallet was not detected. Make sure the extension is enabled, then reload.'
  if (/chain.*not configured|unsupported chain/i.test(m))
    return 'Your wallet is on a network this app does not support. Switch networks and try again.'
  return m
}

export function switchErrorMessage(error: Error, chainName: string): string {
  const m = error.message
  if (/user rejected|user denied/i.test(m)) return 'Network switch rejected.'
  if (/4902|unrecognized chain|add.*chain/i.test(m))
    return `Add ${chainName} to your wallet, then try again.`
  return `Could not switch to ${chainName}.`
}

// Sign-in copy lives with the auth hook now — see `signInFailureReason` in
// hooks/useAuth.ts. It is worded for a failure that blocks nothing.
